import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const BOOK_COVER = "https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/b61a60ddab35f9b0af6b0aead5859bb1.jpg";

const formats = [
  {
    icon: FiIcons.FiSmartphone,
    title: "Kindle Edition",
    price: "$2.99",
    desc: "Instant delivery to your Kindle device or app. Read on any device, anywhere.",
    features: ["Read on any device", "Instant delivery", "Searchable full text"],
    cta: "Buy Kindle",
    highlight: false,
  },
  {
    icon: FiIcons.FiBookOpen,
    title: "Paperback",
    price: "$9.99",
    desc: "The classic workbook feel. Great for annotating and keeping on your desk as a working reference.",
    features: ["High-quality print", "Great for highlighting", "Ideal desk reference"],
    cta: "Buy Paperback",
    highlight: true,
    badge: "Recommended",
  },
  {
    icon: FiIcons.FiBook,
    title: "Hardcover",
    price: "$13.99",
    desc: "Premium paper and binding built to last. The ultimate durable reference copy.",
    features: ["Durable hardback cover", "Premium paper quality", "Built to last"],
    cta: "Buy Hardcover",
    highlight: false,
  },
];

export default function Buy() {
  return (
    <div className="min-h-[80vh] bg-slate-50 py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-14">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">Buy Website Blueprint Foundation: Volume 1</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Choose the format that fits how you read, study, and build. Available securely through Amazon and select retailers.
          </p>
        </motion.div>

        {/* Book cover + trust copy */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 md:p-10 mb-12 flex flex-col md:flex-row gap-8 items-center">
          <img src={BOOK_COVER} alt="Website Blueprint Foundation Book Cover" className="w-full max-w-[180px] h-auto rounded-lg shadow-lg flex-shrink-0" />
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Website Blueprint Foundation: Volume 1</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              This volume is designed to be used, not just read. Its checklist-plus-explanation format makes it useful both as a first complete read and as a working reference throughout a real website project.
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              {["Domains & Hosting", "HTML & CSS", "WordPress Setup", "Accessibility", "SEO Foundations", "Launch Readiness"].map((tag) => (
                <span key={tag} className="bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 rounded-full font-medium">{tag}</span>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {formats.map((f, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + idx * 0.1 }}
              className={`rounded-2xl p-8 flex flex-col relative ${f.highlight ? 'bg-blue-600 border border-blue-500 shadow-2xl transform md:-translate-y-4 text-white' : 'bg-white border border-slate-200 shadow-sm'}`}>
              {f.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-yellow-900 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide shadow-sm">{f.badge}</div>
              )}
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-5 ${f.highlight ? 'bg-blue-500' : 'bg-slate-100'}`}>
                <SafeIcon icon={f.icon} className={`text-xl ${f.highlight ? 'text-white' : 'text-slate-600'}`} />
              </div>
              <h3 className={`text-xl font-bold mb-1 ${f.highlight ? 'text-white' : 'text-slate-900'}`}>{f.title}</h3>
              <div className={`text-4xl font-extrabold mb-3 ${f.highlight ? 'text-white' : 'text-slate-900'}`}>{f.price}</div>
              <p className={`text-sm mb-6 flex-grow leading-relaxed ${f.highlight ? 'text-blue-100' : 'text-slate-600'}`}>{f.desc}</p>
              <ul className="space-y-2 mb-8">
                {f.features.map((feat, i) => (
                  <li key={i} className={`flex items-center gap-2 text-sm ${f.highlight ? 'text-blue-100' : 'text-slate-500'}`}>
                    <SafeIcon icon={FiIcons.FiCheck} className={f.highlight ? 'text-white' : 'text-blue-500'} /> {feat}
                  </li>
                ))}
              </ul>
              <button className={`w-full py-3 px-4 rounded-lg font-bold transition-colors flex justify-center items-center gap-2 ${f.highlight ? 'bg-white text-blue-600 hover:bg-slate-50' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>
                {f.cta} <SafeIcon icon={FiIcons.FiExternalLink} className="h-4 w-4" />
              </button>
            </motion.div>
          ))}
        </div>

        <div className="text-center text-slate-500 text-sm">
          <p>Also available for bulk purchase for classrooms or agencies. <a href="#" className="text-blue-600 hover:underline">Contact the author</a> for details.</p>
        </div>
      </div>
    </div>
  );
}